/**
 * @fileoverview Navigation and view switching for the Period Calculator.
 * Handles month navigation and circular/calendar view toggle.
 * @module period-tracker/period-navigation
 */

/* global femcare */

femcare.periodTracker = femcare.periodTracker || {};

/**
 * @namespace femcare.periodTracker.navigation
 * @description Manages month navigation and view switching.
 */
femcare.periodTracker.navigation = {
    currentMonth: null,
    currentView: 'circular',
    monthDisplay: null,
    previousMonthButton: null,
    nextMonthButton: null,
    circularViewElement: null,
    calendarViewElement: null,
    circularViewToggleButton: null,
    calendarViewToggleButton: null,
    monthNames: null,
    onMonthChange: null,
    onViewChange: null,

    /**
     * Initializes the navigation module.
     * @param {Object} config - Configuration object
     * @param {HTMLElement} config.monthDisplay - Month display element
     * @param {HTMLElement} config.previousMonthButton - Previous month button
     * @param {HTMLElement} config.nextMonthButton - Next month button
     * @param {HTMLElement} config.circularView - Circular view element
     * @param {HTMLElement} config.calendarView - Calendar view element
     * @param {HTMLElement} config.circularViewToggleButton - Circular view toggle button
     * @param {HTMLElement} config.calendarViewToggleButton - Calendar view toggle button
     * @param {string[]} config.monthNames - Localized month names
     * @param {Function} config.onMonthChange - Month change callback
     * @param {Function} config.onViewChange - View change callback
     */
    init: function (config) {
        this.currentMonth = new Date();
        this.currentMonth.setDate(1);

        this.monthDisplay = config.monthDisplay;
        this.previousMonthButton = config.previousMonthButton;
        this.nextMonthButton = config.nextMonthButton;
        this.circularViewElement = config.circularView;
        this.calendarViewElement = config.calendarView;
        this.circularViewToggleButton = config.circularViewToggleButton;
        this.calendarViewToggleButton = config.calendarViewToggleButton;
        this.monthNames = config.monthNames;
        this.onMonthChange = config.onMonthChange;
        this.onViewChange = config.onViewChange;

        this.bindEvents();
        this.updateMonthDisplay();
        this.updateButtonStates();
        this.updateViewDisplay();
    },

    /**
     * Binds navigation event listeners.
     */
    bindEvents: function () {
        if (this.previousMonthButton) {
            this.previousMonthButton.addEventListener('click', () => this.goToPreviousMonth());
        }

        if (this.nextMonthButton) {
            this.nextMonthButton.addEventListener('click', () => this.goToNextMonth());
        }

        if (this.circularViewToggleButton) {
            this.circularViewToggleButton.addEventListener('click', () => this.switchView('circular'));
        }

        if (this.calendarViewToggleButton) {
            this.calendarViewToggleButton.addEventListener('click', () => this.switchView('calendar'));
        }
    },

    /**
     * Navigates to the previous month.
     */
    goToPreviousMonth: function () {
        if (this.isPreviousMonthDisabled()) {
            return;
        }

        this.currentMonth.setMonth(this.currentMonth.getMonth() - 1);
        this.updateMonthDisplay();
        this.updateButtonStates();

        if (this.onMonthChange) {
            this.onMonthChange(this.currentMonth);
        }
    },

    /**
     * Navigates to the next month.
     */
    goToNextMonth: function () {
        if (this.isNextMonthDisabled()) {
            return;
        }

        this.currentMonth.setMonth(this.currentMonth.getMonth() + 1);
        this.updateMonthDisplay();
        this.updateButtonStates();

        if (this.onMonthChange) {
            this.onMonthChange(this.currentMonth);
        }
    },

    /**
     * Checks if previous month navigation is disabled.
     * @returns {boolean} True if disabled
     */
    isPreviousMonthDisabled: function () {
        const cycleLogic = femcare.periodTracker.cycleLogic;
        const prevMonth = new Date(this.currentMonth);
        prevMonth.setMonth(this.currentMonth.getMonth() - 1);
        return cycleLogic.isMonthDisabled(prevMonth, 'prev');
    },

    /**
     * Checks if next month navigation is disabled.
     * @returns {boolean} True if disabled
     */
    isNextMonthDisabled: function () {
        const cycleLogic = femcare.periodTracker.cycleLogic;
        const nextMonth = new Date(this.currentMonth);
        nextMonth.setMonth(this.currentMonth.getMonth() + 1);
        return cycleLogic.isMonthDisabled(nextMonth, 'next');
    },

    /**
     * Updates navigation button states.
     */
    updateButtonStates: function () {
        if (this.previousMonthButton) {
            const disabled = this.isPreviousMonthDisabled();
            this.previousMonthButton.disabled = disabled;
            this.previousMonthButton.classList.toggle('disabled', disabled);
        }

        if (this.nextMonthButton) {
            const disabled = this.isNextMonthDisabled();
            this.nextMonthButton.disabled = disabled;
            this.nextMonthButton.classList.toggle('disabled', disabled);
        }
    },

    /**
     * Updates the month display text.
     */
    updateMonthDisplay: function () {
        if (!this.monthDisplay) {
            return;
        }

        this.monthDisplay.textContent = femcare.shared.dateFormat.formatMonthYear(this.currentMonth);
    },

    /**
     * Switches between circular and calendar views.
     * @param {string} viewName - 'circular' or 'calendar'
     */
    switchView: function (viewName) {
        if (viewName !== 'circular' && viewName !== 'calendar') {
            return;
        }

        if (this.currentView === viewName) {
            return;
        }

        this.currentView = viewName;
        this.updateViewDisplay();

        if (this.onViewChange) {
            this.onViewChange(viewName);
        }
    },

    /**
     * Updates view visibility and button states.
     */
    updateViewDisplay: function () {
        requestAnimationFrame(() => {
            if (this.circularViewElement && this.calendarViewElement) {
                const isCircular = this.currentView === 'circular';
                this.circularViewElement.classList.toggle('hidden', !isCircular);
                this.calendarViewElement.classList.toggle('hidden', isCircular);
            }

            if (this.circularViewToggleButton && this.calendarViewToggleButton) {
                const isCircular = this.currentView === 'circular';
                this.circularViewToggleButton.classList.toggle('period-calendar__view-btn--active', isCircular);
                this.calendarViewToggleButton.classList.toggle('period-calendar__view-btn--active', !isCircular);
            }

            this.updateInstructionLabels();
        });
    },

    /**
     * Updates instruction labels based on current view.
     */
    updateInstructionLabels: function () {
        const circularLabel = document.querySelector('.period-calendar__selected-day-label--circular');
        const calendarLabel = document.querySelector('.period-calendar__selected-day-label--calendar');

        if (circularLabel && calendarLabel) {
            const isCircular = this.currentView === 'circular';
            circularLabel.classList.toggle('hidden', !isCircular);
            calendarLabel.classList.toggle('hidden', isCircular);
        }
    },

    /**
     * Gets the total days in current month.
     * @returns {number} Number of days
     */
    getTotalDaysInMonth: function () {
        return new Date(
            this.currentMonth.getFullYear(),
            this.currentMonth.getMonth() + 1,
            0
        ).getDate();
    },

    /**
     * Gets the current month.
     * @returns {Date} Current month
     */
    getCurrentMonth: function () {
        return new Date(this.currentMonth);
    },

    /**
     * Sets the current month.
     * @param {Date} month - Month to set
     */
    setCurrentMonth: function (month) {
        this.currentMonth = new Date(month);
        this.currentMonth.setDate(1);
        this.updateMonthDisplay();
        this.updateButtonStates();
    }
};
